import Customer from '@sunbit-dev/common/models/ICustomer';
import OnlineApplicationMerchant from '@sunbit-dev/common/models/IOnlineApplicationMerchant';
import Offer from '@sunbit-dev/common/models/IOffer';
import Address from '@sunbit-dev/common/models/IAddress';
import { isString } from '../utilities/string';

export const maskPhoneNumber = (phoneNumber?: string) =>
  !phoneNumber ? 'empty' : `(***)***.${phoneNumber?.substring(phoneNumber?.length - 4, phoneNumber?.length)}`;

function maskAddressDetails(addressDetails: Address) {
  return { state: addressDetails?.state };
}

const mask = ({ str, allowedLettersCount }: { str: string; allowedLettersCount: number }): string => {
  if (!isString(str)) {
    console.error('Value to mask is not string');
    return '';
  }

  if (!str) return 'empty';

  return str.substring(0, allowedLettersCount) + '*'.repeat(str.length - (allowedLettersCount || 0));
};

const maskEmail = (email: string): string => {
  if (!email) return 'empty';

  const [name, domain] = email.split('@');
  const [host, suffix] = domain.split('.');

  return `${mask({ str: name || '', allowedLettersCount: 1 })}@${mask({
    str: host,
    allowedLettersCount: 1,
  })}.${suffix}`;
};

export const getCustomerForLogs = (customer: Partial<Customer>): any => {
  const {
    id,
    existingCustomer,
    addressDetails,
    firstName,
    lastName,
    phone,
    consumerAlertFlagActivated,
    emailVerified,
    wasCustomerMergedDuringProcess,
    customerDetailsOrigin,
    email,
  } = customer || {};

  return {
    email: maskEmail(email),
    id,
    existingCustomer,
    addressDetails: maskAddressDetails(addressDetails),
    firstName: mask({ str: firstName || '', allowedLettersCount: 2 }),
    lastName: mask({ str: lastName || '', allowedLettersCount: 2 }),
    phone: maskPhoneNumber(phone),
    consumerAlertFlagActivated,
    emailVerified,
    wasCustomerMergedDuringProcess,
    customerDetailsOrigin,
  };
};

export const getPurchaseLogsInfo = (purchaseInfo: OnlineApplicationMerchant): Partial<OnlineApplicationMerchant> => {
  const {
    customerPhoneNumber,
    merchantVertical,
    merchantType,
    origin,
    onlinePurchaseSupportedTypes,
    merchantOriginatorActiveStates,
    merchantOriginator,
    merchantId,
    chainFeatures,
    amountRequested,
    applicationAmountFinancedLowerLimit,
    applicationAmountLowerLimit,
    applicationAmountUpperLimit,
    merchantAprLowerLimit,
    merchantAprUpperLimit,
  } = purchaseInfo || {};

  return {
    customerPhoneNumber: maskPhoneNumber(customerPhoneNumber),
    merchantVertical,
    merchantType,
    origin,
    onlinePurchaseSupportedTypes,
    merchantOriginatorActiveStates,
    merchantOriginator,
    merchantId,
    chainFeatures,
    amountRequested,
    applicationAmountFinancedLowerLimit,
    applicationAmountLowerLimit,
    applicationAmountUpperLimit,
    merchantAprLowerLimit,
    merchantAprUpperLimit,
  };
};

export const getOfferLogsInfo = (offer: Offer): Partial<Offer> => {
  const { offerId, amountRequested, maxAmountApproved, expirationDate } = offer || {};

  return {
    offerId,
    amountRequested,
    maxAmountApproved,
    expirationDate,
  };
};

const knownErrorCodes: { [key: string]: boolean } = {
  wrongCode: true,
  expiredCode: true,
  customerPhoneNotMatching: true,
  'LinkExpiredErrorCode:COMPLETED_PURCHASE': true,
  'LinkExpiredErrorCode:EXPIRED_LINK': true,
  RefreshTokenAuthenticationFailed: true,
  TooManyProfiles: true,
  verificationCodeNeeded: true,
  alreadyVerified: true,
  phoneNumberNotMatching: true,
  exceededVerificationAttempts: true,
  ageRestriction: true,
  ageRestriction19: true,
  leftMaxAmountsBeforeTaxErrorCode: true,
  onlineCancellationErrorCode: true,
  ofacPendingError: true,
  ofacFinalError: true,
  ssnRequired: true,
  duplicateSSN: true,
  ssnVerificationFailedTooManyTimes: true,
  consumerAlert: true,
  purchaseFinanceAmount: true,
  numberOfAllowedActiveLoansExceeded: true,
  ILLEGAL_ARGUMENT: true,
  'rejectionErrorCode:140': true,
  'rejectionErrorCode:6': true,
  'rejectionErrorCode:2': true,
  'rejectionErrorCode:145': true,
  'rejectionErrorCode:160': true,
  'rejectionErrorCode:165': true,
  InvalidRoutingNumber: true,
  ssnNotValid: true,
  'CancellationErrorCode:10': true,
  'CancellationErrorCode:210': true,
  'CancellationErrorCode:6': true,
  'CancellationErrorCode:22': true,
  'CancellationErrorCode:102': true,
  'CancellationErrorCode:116': true,
  'CancellationErrorCode:120': true,
  'CancellationErrorCode:130': true,
  'CancellationErrorCode:132': true,
  'CancellationErrorCode:230': true,
  'CancellationErrorCode:300': true,
  RetailerAmountWaitingTimeExceeded: true,
  extraVerificationFlowRequired: true,
  ErrorEmail: true,
  extraVerificationRequired: true,
  customerVerificationFlowRequired: true,
  manualExtraVerificationFallback: true,
  PHONE_NUMBER_REQUIRED: true,
  invalidEmail: true
};

export const checkIfKnownError = (errorCode?: string): boolean => {
  return !!knownErrorCodes[errorCode];
};
